/**
 * @file ClassificationCodeField.js
 * @description field with check icon for approved classification option.
 *              displays classification.description in toolTip
 *
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 *
 * https://mui.com/components/autocomplete/
 * https://stackoverflow.com/questions/22928841/lodash-multi-column-sortby
 * https://stackoverflow.com/questions/69395945/how-can-i-add-unique-keys-to-react-mui-autocomplete-component
 * 
 *  options structure:
 *  [
        {
            "classificationCode": "",
            "name": "",
            "category": "",
            "desc": "",
            "approved": false,
        },
    ]
 *   
 * @author don michael
 * @since Jan 11, 2022 13:54
 */
import _ from 'lodash';

import { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Chip from '@mui/material/Chip';

import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
// import Typography from '@mui/material/Typography';

import {
    arrayExists,
    objectExists,
    objectKeyExists,
    stringExists
} from 'src/@common/utilities';

function ClassificationCodeField(props) {
    const {
        fieldName = 'classification',
        fieldLabel = 'Classification',
        currentValue,  // string
        options = [],
        fieldErrors = {},
        helpText = '',
        isFocus = false,
        // style for autocomplete
        sxStyle = {
            width: '280px',
        },
        //style for internal input field
        inputSxStyle = {
            marginLeft: '-8px',
        },
        handleBlur = () => { },
        handleChange,
    } = props;

    const emptyValue = {
        classificationCode: '',
        name: '',
        category: '',
        desc: '',
        approved: false,
    };

    const [isApproved, setIsApproved] = useState(false);
    const [open, setOpen] = useState(false);
    const [descText, setDescText] = useState();
    const [sortedOptions, setSortedOptions] = useState([]);

    const [value, setValue] = useState(emptyValue);

    const noOptionsText = 'No classifications found';

    useEffect(() => {
        if (stringExists(currentValue) &&
            arrayExists(options)) {

            const match = options.find((item) => {
                return item.classificationCode === currentValue;
            });

            if (match) {
                // set the tooltip description
                setDescText(match.desc);

                // determine if the currentValue is an approved classification
                if (objectKeyExists(match, 'approved') && match.approved) {
                    setIsApproved(true);
                } else {
                    setIsApproved(false);
                }

                setValue(match);
            }

        } else {
            setValue({
                classificationCode: '',
                name: '',
                category: '',
                desc: '',
                approved: false,
            });
            setDescText('')
        }

    }, [currentValue, options]);

    useEffect(() => {
        if (arrayExists(options)) {
            const sortedOptions = [];

            // push the provided options in
            options.forEach((item) => {
                sortedOptions.push(item);
            });

            // sort options by: approved + classificationCode
            setSortedOptions(_.orderBy(sortedOptions, ['approved', 'name'], ['desc', 'asc']));
        }
    }, [options]);

    const onInputChange = (event, newValue, reason) => {
        // use this changeEvent to identify keystroke input,

        let newEvent = null;

        switch (reason) {

            case 'clear':

                newEvent = {
                    target: {
                        type: 'autocomplete',
                        value: '',
                    }
                };

                setIsApproved(false);
                setDescText('');

                // call the parent change handler
                handleChange(newEvent, fieldName);

                break;

            case 'reset':
                break;

            case 'input':
                // update local state and inform the parent of the change
                onChange(null, newValue);
                break;

            default:
                break;
        }
    };

    const onChange = (event, newValue) => {

        let classificationCode = '';

        // set the local state
        if (newValue) {
            classificationCode = newValue.classificationCode;
            setValue(newValue);
            setDescText(newValue.desc);

            // determine if the currentValue is an approved classification
            if (objectKeyExists(newValue, 'approved') && newValue.approved) {
                setIsApproved(true);
            } else {
                setIsApproved(false);
            }

        } else {
            setValue(emptyValue);
        }

        // wrap in a synthetic event
        const newEvent = {
            target: {
                type: 'autocomplete',
                value: classificationCode,
            }
        };

        // call the parent change handler
        handleChange(newEvent, fieldName);
    };

    const onBlur = () => {

        if (stringExists(value.classificationCode)) {

            const match = options.find((item) => {
                return item.classificationCode === value.classificationCode;
            });

            if (match) {
                setValue(match);
                onChange(null, match);
            } else {
                setValue(emptyValue);
                onChange(null, emptyValue);
            }

        } else {
            setValue(emptyValue);
            onChange(null, emptyValue);
        }

        handleBlur(fieldName);
    };

    return (
        <Fragment>

            <Stack
                ml={0}
                direction={{ xs: 'column', sm: 'column' }}
                spacing={{ xs: 0, sm: 0, md: 0 }}
                justifyContent="flex-start"
            >
                <Autocomplete
                    id={fieldName}
                    name={fieldName}
                    sx={sxStyle}
                    disableClearable
                    disablePortal={true}
                    ListboxProps={{ style: { maxHeight: '15rem' } }}
                    open={open}
                    onOpen={() => {
                        setOpen(true);
                    }}
                    onClose={() => {
                        setOpen(false);
                    }}
                    getOptionDisabled={(option) =>
                        option.classificationCode === ''
                    }
                    getOptionLabel={(option) => {
                        return typeof option === 'string' ? option : option.name
                    }}
                    includeInputInList

                    isOptionEqualToValue={(option, value) => {
                        if (objectExists(value) &&
                            stringExists(value.value)) {
                            return option.value === value.value
                        } else {
                            return true;
                        }
                    }}
                    onBlur={() => {
                        onBlur();
                    }}
                    onInputChange={onInputChange}
                    onChange={onChange}
                    options={sortedOptions}
                    noOptionsText={noOptionsText}
                    value={value}
                    renderOption={(props, option) => {
                        return (<Box component="li" sx={{ mr: 2 }}  {...props} key={option.name}>
                            {objectKeyExists(option, 'approved') && option.approved &&
                                <CheckCircleOutlineIcon sx={{ mr: 1 }} color={'success'} />
                            }
                            {option.name}
                        </Box>
                        )
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={fieldLabel}
                            sx={inputSxStyle}
                            size="small"
                            margin="dense"
                            variant="filled"
                            error={Boolean(fieldErrors[fieldName])}
                            autoFocus={isFocus}
                            helperText={descText ? descText : helpText}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <>
                                        <InputAdornment position="start">
                                            {isApproved &&
                                                <CheckCircleOutlineIcon sx={{ mb: 2 }} color={'success'} />
                                            }
                                        </InputAdornment>
                                        {params.InputProps.startAdornment}
                                    </>
                                )
                            }}

                        />
                    )}
                />

            </Stack>

        </Fragment >
    )
}

ClassificationCodeField.propTypes = {
    fieldLabel: PropTypes.string,
    fieldName: PropTypes.string,
    currentValue: PropTypes.string,
    options: PropTypes.array,
    fieldErrors: PropTypes.object,
    helpText: PropTypes.string,
    isFocus: PropTypes.bool,
    sxStyle: PropTypes.object,
    inputSxStyle: PropTypes.object,
    handleBlur: PropTypes.func,
    handleChange: PropTypes.func.isRequired,
};

export default ClassificationCodeField;
